import React, { useState, useEffect, useCallback } from 'react';
import { GmailSettings } from './GmailSettings';

import {
    Box,
    Typography,
    Paper,
    Chip,
    Button,
    CircularProgress,
    IconButton,

} from '@material-ui/core';
import { Edit, Delete, Check, Refresh, Visibility, Block as BlockIcon } from '@material-ui/icons';
import {
    getDrafts,
    acceptDraft,
    dismissDraft,
    editDraft,
    bulkAcceptDrafts,
    syncGmail,
    blockSender,
    ImportDraft,
} from '../core/services/import.service';
import { DraftReconcileDialog } from './DraftReconcileDialog';
import { EmailBodyDialog } from './EmailBodyDialog';

interface ImportQueueProps {
    userId: string;
    listId: string;
    existingCategories: string[];
    existingTags: string[];
    entries: any[];
    onEntriesChanged: () => void;
}

// Maps a confidence score to a color dot
const confidenceColor = (score: number): string => {
    if (score >= 0.8) return '#16a34a';
    if (score >= 0.5) return '#f59e0b';
    return '#dc2626';
};

interface DraftRowProps {
    draft: ImportDraft;
    onAccept: (id: string) => void;
    onDismiss: (id: string) => void;
    onEditClick: (draft: ImportDraft) => void;
    onViewEmail: (draft: ImportDraft) => void;
    onBlockSender: (draft: ImportDraft) => void;
}

const DraftRow: React.FC<DraftRowProps> = ({ draft, onAccept, onDismiss, onEditClick, onViewEmail, onBlockSender }) => {
    const [snippetExpanded, setSnippetExpanded] = useState(false);

    const snippet = draft.rawSnippet || '';
    const snippetDisplay = snippetExpanded ? snippet : snippet.slice(0, 80);
    const hasMoreSnippet = snippet.length > 80;

    const amountColor = draft.type === 'DEBIT' ? '#dc2626' : '#16a34a';

    return (
        <Box
            style={{
                borderBottom: '1px solid #e2e8f0',
                padding: '10px 12px',
                backgroundColor: 'white',
            }}
        >
            {/* Top row: source chip + confidence + amount + actions */}
            <Box style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                <Chip
                    label={draft.source}
                    size="small"
                    style={{
                        fontSize: '0.65rem',
                        height: 18,
                        backgroundColor: '#ede9fe',
                        color: '#7c3aed',
                        fontWeight: 700,
                    }}
                />
                {/* Confidence dot */}
                <Box
                    title={`Confidence: ${Math.round(draft.confidence * 100)}%`}
                    style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: confidenceColor(draft.confidence),
                        flexShrink: 0,
                    }}
                />
                <Typography style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                    {Math.round(draft.confidence * 100)}%
                </Typography>

                <Box style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Typography
                        style={{
                            fontSize: '0.85rem',
                            fontWeight: 700,
                            color: amountColor,
                            marginRight: 4,
                        }}
                    >
                        {draft.type === 'DEBIT' ? '-' : '+'}${Number(draft.amount).toFixed(2)}
                    </Typography>
                    <IconButton
                        size="small"
                        onClick={() => onViewEmail(draft)}
                        title="View email"
                        style={{ padding: 3, color: '#64748b' }}
                        aria-label="view email"
                    >
                        <Visibility style={{ fontSize: 16 }} />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={() => onEditClick(draft)}
                        title="Edit"
                        style={{ padding: 3, color: '#94a3b8' }}
                        aria-label="edit draft"
                    >
                        <Edit style={{ fontSize: 16 }} />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={() => onAccept(draft.id)}
                        title="Accept"
                        style={{ padding: 3, color: '#16a34a' }}
                        aria-label="accept draft"
                    >
                        <Check style={{ fontSize: 16 }} />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={() => onDismiss(draft.id)}
                        title="Dismiss"
                        style={{ padding: 3, color: '#dc2626' }}
                        aria-label="dismiss draft"
                    >
                        <Delete style={{ fontSize: 16 }} />
                    </IconButton>
                    {draft.senderDomain && (
                        <IconButton
                            size="small"
                            onClick={() => onBlockSender(draft)}
                            title={`Block ${draft.senderDomain}`}
                            style={{ padding: 3, color: '#94a3b8' }}
                            aria-label="block sender"
                        >
                            <BlockIcon style={{ fontSize: 16 }} />
                        </IconButton>
                    )}
                </Box>
            </Box>

            {/* Read mode: title + date + category */}
            <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                <Typography style={{ fontSize: '0.8rem', color: '#1e293b', fontWeight: 500, flex: '1 1 auto' }}>
                    {draft.title}
                </Typography>
                <Typography style={{ fontSize: '0.72rem', color: '#64748b' }}>
                    {draft.date}
                </Typography>
                {draft.category && (
                    <Chip
                        label={draft.category}
                        size="small"
                        style={{ fontSize: '0.65rem', height: 18, backgroundColor: '#f1f5f9', color: '#475569' }}
                    />
                )}
            </Box>

            {/* Raw snippet */}
            {snippet && (
                <Box style={{ marginTop: 4 }}>
                    <Typography style={{ fontSize: '0.68rem', color: '#94a3b8', fontStyle: 'italic' }}>
                        {snippetDisplay}
                        {hasMoreSnippet && !snippetExpanded && '…'}
                        {hasMoreSnippet && (
                            <span
                                onClick={() => setSnippetExpanded(e => !e)}
                                style={{ color: '#7c3aed', cursor: 'pointer', marginLeft: 4, fontStyle: 'normal' }}
                            >
                                {snippetExpanded ? 'less' : 'more'}
                            </span>
                        )}
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

/**
 * ImportQueue displays Gmail-imported transaction drafts pending user review.
 * Users can accept, dismiss, or open a reconcile dialog for each draft.
 */
export const ImportQueue: React.FC<ImportQueueProps> = ({
    userId,
    listId,
    existingCategories,
    existingTags,
    entries,
    onEntriesChanged,
}) => {
    const [drafts, setDrafts] = useState<ImportDraft[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [syncing, setSyncing] = useState(false);
    const [bulkLoading, setBulkLoading] = useState(false);

    // Dialog state
    const [dialogDraft, setDialogDraft] = useState<ImportDraft | null>(null);
    const [emailViewDraft, setEmailViewDraft] = useState<ImportDraft | null>(null);

    const fetchDrafts = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await getDrafts(userId, 'pending');
            const rawDrafts = Array.isArray(result?.drafts) ? result.drafts : [];
            setDrafts(rawDrafts);
        } catch {
            setError('Failed to load import drafts. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [userId]);

    useEffect(() => {
        fetchDrafts();
    }, [fetchDrafts]);

    const handleAccept = async (id: string) => {
        try {
            await acceptDraft(id, listId);
            setDrafts(prev => prev.filter(d => d.id !== id));
            onEntriesChanged();
        } catch {
            setError('Failed to accept draft.');
        }
    };

    const handleDismiss = async (id: string) => {
        try {
            await dismissDraft(id);
            setDrafts(prev => prev.filter(d => d.id !== id));
        } catch {
            setError('Failed to dismiss draft.');
        }
    };

    // Called from DraftReconcileDialog when user clicks Accept
    const handleDialogAccept = async (
        draftId: string,
        acceptListId: string,
        editedValues?: { title: string; amount: number; type: 'DEBIT' | 'CREDIT'; date?: string; category?: string; tag?: string; onCredit?: boolean },
    ) => {
        setDialogDraft(null);
        try {
            // If the user edited values via the form, update the draft first
            if (editedValues) {
                await editDraft(draftId, editedValues);
            }
            await acceptDraft(draftId, acceptListId);
            setDrafts(prev => prev.filter(d => d.id !== draftId));
            onEntriesChanged();
        } catch {
            setError('Failed to accept draft.');
        }
    };

    const handleDialogDismiss = async (draftId: string) => {
        setDialogDraft(null);
        try {
            await dismissDraft(draftId);
            setDrafts(prev => prev.filter(d => d.id !== draftId));
        } catch {
            setError('Failed to dismiss draft.');
        }
    };

    const handleSync = async () => {
        setSyncing(true);
        setError(null);
        try {
            await syncGmail(userId);
            await fetchDrafts();
        } catch {
            setError('Gmail sync failed. Please try again.');
        } finally {
            setSyncing(false);
        }
    };

    const handleBulkAcceptAll = async () => {
        const ids = drafts.map(d => d.id);
        if (ids.length === 0) return;
        setBulkLoading(true);
        try {
            await bulkAcceptDrafts(ids, listId);
            setDrafts([]);
            onEntriesChanged();
        } catch {
            setError('Bulk accept failed. Please try again.');
        } finally {
            setBulkLoading(false);
        }
    };

    const handleBulkAcceptHighConfidence = async () => {
        const highConfidenceDrafts = drafts.filter(d => d.confidence >= 0.8);
        const ids = highConfidenceDrafts.map(d => d.id);
        if (ids.length === 0) return;
        setBulkLoading(true);
        try {
            await bulkAcceptDrafts(ids, listId);
            setDrafts(prev => prev.filter(d => d.confidence < 0.8));
            onEntriesChanged();
        } catch {
            setError('Bulk accept failed. Please try again.');
        } finally {
            setBulkLoading(false);
        }
    };

    const handleBlockSender = async (draft: ImportDraft) => {
        const domain = String(draft.senderDomain || '');
        if (!domain) return;
        try {
            await blockSender(userId, domain);
            await fetchDrafts();
        } catch {
            setError('Failed to block sender.');
        }
    };

    const highConfidenceCount = drafts.filter(d => d.confidence >= 0.8).length;

    return (
        <>
            <Paper
                style={{
                    borderRadius: 8,
                    border: '1px solid #e2e8f0',
                    overflow: 'hidden',
                    marginBottom: 8,
                }}
                elevation={0}
            >
                {/* Bulk action bar */}
                <Box
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '8px 12px',
                        backgroundColor: '#faf5ff',
                        borderBottom: '1px solid #e2e8f0',
                        flexWrap: 'wrap',
                    }}
                >
                    <Typography style={{ fontSize: '0.72rem', fontWeight: 700, color: '#7c3aed', marginRight: 4 }}>
                        {loading ? '…' : drafts.length} pending
                    </Typography>

                    <Button
                        size="small"
                        variant="outlined"
                        onClick={handleBulkAcceptAll}
                        disabled={bulkLoading || loading || drafts.length === 0}
                        style={{
                            fontSize: '0.72rem',
                            textTransform: 'none',
                            borderColor: '#7c3aed',
                            color: '#7c3aed',
                            padding: '2px 8px',
                        }}
                    >
                        Accept all
                    </Button>

                    <Button
                        size="small"
                        variant="outlined"
                        onClick={handleBulkAcceptHighConfidence}
                        disabled={bulkLoading || loading || highConfidenceCount === 0}
                        style={{
                            fontSize: '0.72rem',
                            textTransform: 'none',
                            borderColor: '#16a34a',
                            color: '#16a34a',
                            padding: '2px 8px',
                        }}
                    >
                        Accept high-confidence ({highConfidenceCount})
                    </Button>

                    <Box style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
                        {syncing && <CircularProgress size={14} style={{ color: '#7c3aed' }} />}
                        <Button
                            size="small"
                            onClick={handleSync}
                            disabled={syncing}
                            startIcon={<Refresh style={{ fontSize: 14 }} />}
                            style={{
                                fontSize: '0.72rem',
                                textTransform: 'none',
                                color: '#475569',
                                padding: '2px 8px',
                            }}
                        >
                            Sync Gmail
                        </Button>
                    </Box>
                </Box>

                {/* Error banner */}
                {error && (
                    <Box style={{ padding: '6px 12px', backgroundColor: '#fee2e2', borderBottom: '1px solid #fecaca' }}>
                        <Typography style={{ fontSize: '0.75rem', color: '#dc2626' }}>{error}</Typography>
                    </Box>
                )}

                {/* Gmail connection settings */}
                <GmailSettings userId={userId} />

                {/* Content */}
                {loading ? (
                    <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '24px 0' }}>
                        <CircularProgress size={24} style={{ color: '#7c3aed' }} />
                    </Box>
                ) : drafts.length === 0 ? (
                    <Box style={{ textAlign: 'center', padding: '24px 20px', color: '#94a3b8' }}>
                        <Typography style={{ fontSize: '0.85rem' }}>No pending imports</Typography>
                    </Box>
                ) : (
                    <Box style={{ maxHeight: 480, overflowY: 'auto' }}>
                        {drafts.map(draft => (
                            <DraftRow
                                key={draft.id}
                                draft={draft}
                                onAccept={handleAccept}
                                onDismiss={handleDismiss}
                                onEditClick={setDialogDraft}
                                onViewEmail={setEmailViewDraft}
                                onBlockSender={handleBlockSender}
                            />
                        ))}
                    </Box>
                )}
            </Paper>

            {dialogDraft && (
                <DraftReconcileDialog
                    open={dialogDraft !== null}
                    draft={dialogDraft}
                    listId={listId}
                    entries={entries}
                    existingCategories={existingCategories}
                    existingTags={existingTags}
                    onAccept={handleDialogAccept}
                    onDismiss={handleDialogDismiss}
                    onClose={() => setDialogDraft(null)}
                />
            )}
            {emailViewDraft && (
                <EmailBodyDialog
                    open={emailViewDraft !== null}
                    draftId={emailViewDraft.id}
                    draftTitle={emailViewDraft.title}
                    onClose={() => setEmailViewDraft(null)}
                />
            )}
        </>
    );
};
